import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResetDemoTenantResponse, TenantDemoInfo, TenantDetail } from '@up2d8/contracts';
import { useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { useOrgType } from '../../shared/use-org-type-terms';
import {
  AdminIcon,
  Btn,
  Card,
  CardHead,
  CardPad,
  Field,
  Input,
  Modal,
  Note,
  Tile,
  useAdminToast,
} from '../admin-kit';

/**
 * De demo-inhoud laden: dezelfde code als `demo-inhoud.mjs` en `test-omgevingen.mjs`, maar in de
 * browser en met de eigen sessie (ACC-3). Pas laden wanneer erop geklikt wordt: geen klant draagt
 * die inhoud mee in zijn eerste download.
 */
async function laadVulling() {
  return import('../../../../api/scripts/demo/vulling.mjs');
}

/** Wacht tot de rol Beheerder in een vers bedrijf toegekend is; dat loopt via de eventlus. */
async function wachtOpRechten(api: ReturnType<typeof useSession>['api']): Promise<void> {
  for (let poging = 0; poging < 20; poging += 1) {
    const { response } = await api.GET('/api/v1/groups', {});
    if (response.status === 200) return;
    await new Promise((klaar) => setTimeout(klaar, 1500));
  }
}

/**
 * "Vul met demo-inhoud" en "Terugzetten naar beginstand" in het beheer van een demobedrijf.
 *
 * Bestaat alleen als de server zegt dat het mag (`GET /tenant/demo`): vlag `tenants.demo_tools`
 * aan én een demobedrijf. Een gewone klant ziet hier niets van, ook geen lege kaart.
 *
 * Sinds BHR-2 de demokaart bovenaan Start (S1) en het venster S2: een oranje tegel, de uitleg, en
 * rechts de twee knoppen. De rode knop in het venster werkt pas als de naam exact klopt.
 */
export function DemoToolsPanel() {
  const { t } = useTranslation();
  const { api, profile, getAccessToken, adoptToken } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const geslaagd = useAdminToast();
  const [bezig, setBezig] = useState(false);
  const [voortgang, setVoortgang] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [fout, setFout] = useState<string | null>(null);
  // De demo-inhoud is die van een productiebedrijf (weekmenu, veiligheid); bij een club of een school
  // zou ze meer verwarren dan tonen (ORG-3). Terugzetten kan wel bij elk type.
  const orgType = useOrgType();

  const demo = useQuery({
    queryKey: ['tenant-demo', profile?.tenantId],
    enabled: Boolean(profile?.tenantId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/tenant/demo', {});
      return (data as TenantDemoInfo | undefined) ?? null;
    },
  });

  const info = demo.data;
  // De melding blijft staan: na het terugzetten is er even geen `info`, en "teruggezet" hoort
  // dan toch te verschijnen.
  if (!info?.demoTools) return geslaagd.element;

  const vul = async () => {
    setBezig(true);
    setVoortgang(t('demoTools.filling'));
    try {
      await wachtOpRechten(api);
      const { vulBedrijf } = await laadVulling();
      let regels = 0;
      const uitkomst = await vulBedrijf({
        basis: window.location.origin,
        token: getAccessToken() ?? '',
        tenantCode: profile?.tenantCode ?? '',
        log: () => {
          regels += 1;
          setVoortgang(t('demoTools.fillingProgress', { count: regels }));
        },
      });
      await queryClient.invalidateQueries();
      const titel = t('demoTools.filled', {
        made: uitkomst.gemaakt,
        skipped: uitkomst.overgeslagen,
      });
      // De melding van het portaal kent alleen "gelukt"; een vulling met fouten blijft een
      // waarschuwing in de gewone melding, anders leest ze als een succes.
      if (uitkomst.fouten > 0) toast.show({ title: titel, tone: 'warning' });
      else geslaagd.show(titel);
    } catch {
      toast.show({ title: t('demoTools.fillFailed'), tone: 'danger' });
    } finally {
      setBezig(false);
      setVoortgang(null);
    }
  };

  const sluit = () => {
    setOpen(false);
    setNaam('');
    setWachtwoord('');
    setFout(null);
  };

  const zetTerug = async () => {
    setFout(null);
    setBezig(true);
    try {
      const { data, error, response } = await api.POST('/api/v1/demo/reset', {
        body: { confirmName: naam, password: wachtwoord },
      });
      if (error || !data) {
        setFout(
          response.status === 401
            ? t('demoTools.wrongPassword')
            : response.status === 400
              ? t('demoTools.wrongName')
              : t('demoTools.resetFailed'),
        );
        return;
      }
      const result = data as ResetDemoTenantResponse & { accessToken: string };
      sluit();
      queryClient.clear();
      await adoptToken(result.accessToken);
      geslaagd.show(t('demoTools.resetDone', { name: result.name }));
      void navigate({ to: '/admin' });
    } finally {
      setBezig(false);
    }
  };

  // "Exact": de rode knop werkt pas als de naam letterlijk klopt (S2). De server controleert het
  // nog eens; dit voorkomt alleen een klik die toch geweigerd wordt.
  const naamKlopt = naam.trim() === info.name;

  return (
    <>
      <Card testId="demo-tools">
        <CardPad>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Tile tone="orange">
              <AdminIcon name="sparkle" size={20} />
            </Tile>
            <div style={{ flex: 1, minWidth: 220 }}>
              <h2 className="a-h3">{t('demoTools.title', { name: info.name })}</h2>
              <div className="a-muted">{t('demoTools.intro')}</div>
              {voortgang ? (
                <p className="a-small a-muted" aria-live="polite" style={{ marginTop: 6 }}>
                  {voortgang}
                </p>
              ) : null}
            </div>
            <div className="a-actions">
              {orgType === 'factory' ? (
                <Btn
                  variant="sec"
                  size="sm"
                  icon={<AdminIcon name="sparkle" />}
                  onClick={() => void vul()}
                  disabled={bezig}
                  aria-busy={bezig && !open}
                  testId="demo-fill"
                >
                  {t('demoTools.fill')}
                </Btn>
              ) : null}
              {info.canReset ? (
                <Btn
                  variant="danger-sec"
                  size="sm"
                  icon={<AdminIcon name="rotateCcw" />}
                  onClick={() => setOpen(true)}
                  disabled={bezig}
                  testId="demo-reset"
                >
                  {t('demoTools.reset')}
                </Btn>
              ) : null}
            </div>
          </div>
        </CardPad>
      </Card>

      <Modal
        open={open}
        onClose={sluit}
        title={t('beheer.start.reset.title', { name: info.name })}
        footer={
          <>
            <Btn variant="sec" onClick={sluit}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant="danger"
              icon={<AdminIcon name="rotateCcw" />}
              disabled={bezig || !naamKlopt || wachtwoord.length === 0}
              aria-busy={bezig}
              onClick={() => void zetTerug()}
              testId="demo-reset-confirm"
            >
              {t('demoTools.reset')}
            </Btn>
          </>
        }
      >
        {/* De dubbele punt direct na het vetgedrukte begin, zoals in S2; `lead` zet er een spatie. */}
        <Note tone="danger">
          <strong>{t('beheer.start.reset.lead', { name: info.name })}</strong>
          {t('beheer.start.reset.text')}
        </Note>
        <Field
          label={t('beheer.start.reset.typeName')}
          hint={t('beheer.start.reset.typeNameHint', { name: info.name })}
        >
          {(props) => (
            <Input
              {...props}
              value={naam}
              autoComplete="off"
              onChange={(event) => setNaam(event.target.value)}
              data-testid="demo-reset-name"
            />
          )}
        </Field>
        <Field label={t('demoTools.password')} error={fout ?? undefined}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="current-password"
              value={wachtwoord}
              onChange={(event) => setWachtwoord(event.target.value)}
              data-testid="demo-reset-password"
            />
          )}
        </Field>
      </Modal>
      {geslaagd.element}
    </>
  );
}

/**
 * Terugzetten door de platformbeheerder, ook voor Democorp algemeen (ACC-3). Iedereen die er een
 * account had, krijgt de mail "Toegang bevestigen" voor het verse bedrijf.
 *
 * Sinds BHR-8 de tab "Terugzetten" op het bedrijfsdetail, die er alleen bij een demobedrijf is:
 * een rode knop met wat er gebeurt, en een venster waarin de naam overgetypt wordt.
 */
export function PlatformDemoReset({ tenant }: { tenant: TenantDetail }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [klaar, setKlaar] = useState<string | null>(null);

  if (tenant.kind !== 'shared' && tenant.kind !== 'personal') return null;

  const zetTerug = async () => {
    setBezig(true);
    setFout(null);
    try {
      const { data, error, response } = await api.POST(
        '/api/v1/platform/tenants/{tenantId}/reset',
        {
          params: { path: { tenantId: tenant.id } },
          body: { confirmName: naam },
        },
      );
      if (error || !data) {
        setFout(response.status === 400 ? t('demoTools.wrongName') : t('demoTools.resetFailed'));
        return;
      }
      const result = data as ResetDemoTenantResponse;
      setOpen(false);
      setNaam('');
      await queryClient.invalidateQueries();
      setKlaar(t('demoTools.platformResetDone', { name: result.name, count: result.invited }));
      void navigate({
        to: '/master/organisaties/$tenantId',
        params: { tenantId: result.tenantId },
      });
    } finally {
      setBezig(false);
    }
  };

  return (
    <Card testId="platform-demo-reset">
      <CardHead title={t('demoTools.resetTitle')} />
      <CardPad>
        {klaar ? (
          <Note tone="info" role="status">
            {klaar}
          </Note>
        ) : null}
        <Note tone="attention">{t('demoTools.platformResetIntro')}</Note>
        <div className="a-actions">
          <Btn variant="danger" onClick={() => setOpen(true)}>
            {t('demoTools.reset')}
          </Btn>
        </div>
      </CardPad>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('demoTools.resetTitle')}
        sub={t('demoTools.resetHint', { name: tenant.name })}
        footer={
          <>
            <Btn variant="sec" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Btn>
            <Btn
              variant="danger"
              disabled={bezig || naam.trim().length === 0}
              onClick={() => void zetTerug()}
            >
              {t('demoTools.resetConfirm')}
            </Btn>
          </>
        }
      >
        <Field label={t('demoTools.typeName', { name: tenant.name })} error={fout ?? undefined}>
          {(props) => (
            <Input {...props} value={naam} onChange={(event) => setNaam(event.target.value)} />
          )}
        </Field>
      </Modal>
    </Card>
  );
}
